import { apiRequest } from "../api/baseApi";
import { createContext, useContext, useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";
import { ClientContext } from "./ClientContext";

const ContactContext = createContext(null);

export const ContactProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const { selectedClient, refetchCaregivers } = useContext(ClientContext);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [regContacts, setRegContacts] = useState([]);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const normalizeContact = (contact) => ({
    id: contact.id,
    userId: contact.user_id || contact.userId || contact.id,
    firstName: contact.firstName || contact.first_name || contact.name || "",
    lastName: contact.lastName || contact.last_name || "",
    email: contact.email,
    phone: contact.phone,
    image: contact.photo,
    contactRole: contact.user_type,
    status: contact.status,
    contactTypes: contact.contact_types,
    can_delete: contact.can_delete,
    can_edit: contact.can_edit,
    shared_with_count: contact.shared_with_count || 1,
    shared_with_clients: contact.shared_with_clients || [],
  });

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const res = await apiRequest(`/contacts?clientId=${selectedClient.id}`, {
        method: "GET",
        version: "V1",
      });

      if (!res.ok) {
        throw new Error("Failed to fetch contacts");
      }

      const data = await res.json();
      setContacts(data.map(normalizeContact));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedClient) {
      return;
    }
    fetchContacts();
  }, [selectedClient]);

  const isContactOfType = (contact, type) =>
    contact.contactTypes.includes(type);

  const isEmergencyContact = (contact) =>
    contact.id < 0 || isContactOfType(contact, "emergency");

  useEffect(() => {
    if (!contacts.length) {
      setRegContacts([]);
      setEmergencyContacts([]);
      return;
    }

    const reg = contacts.filter((c) => isContactOfType(c, "regular"));
    const emergency = contacts.filter((c) => isEmergencyContact(c));

    setRegContacts(reg);
    setEmergencyContacts(emergency);
  }, [contacts]);

  const updateContact = async (id, contact) => {
    try {
      const requestBody = {
        ...contact,
        clientId: selectedClient.id,
        name: contact.firstName,
      };

      const res = await apiRequest(`/contacts/${id}`, {
        method: "PUT",
        version: "V1",
        body: requestBody,
      });

      if (!res.ok) {
        const data = await res.json();
        return data.message;
      }

      await fetchContacts();
      await refetchCaregivers();
    } catch (err) {
      console.error("Error updating contact:", err);
      return "Failed to update contact. Please try again.";
    }
  };

  const addContact = async (contact) => {
    try {
      const res = await apiRequest(`/contacts/`, {
        method: "POST",
        version: "V1",
        body: {
          ...contact,
          caregiverId: currentUser.id,
          clientId: selectedClient.id,
          name: contact.firstName,
          status: "inactive",
        },
      });
      if (!res.ok) {
        const data = await res.json();
        return data.message;
      }
      await fetchContacts();
    } catch (err) {
      console.error("Error adding contact:", err);
      return "Failed to add contact. Please try again.";
    }
  };

  const addExistingContact = async ({ email, contactTypes }) => {
    try {
      const res = await apiRequest(`/contacts/add-existing`, {
        method: "POST",
        version: "V1",
        body: {
          email,
          contactTypes,
          clientId: selectedClient.id,
        },
      });

      if (!res.ok) {
        const data = await res.json();
        return data.message;
      }

      await fetchContacts();
    } catch (err) {
      console.error("Error adding existing contact:", err);
    }
  };

  const deleteContact = async (id, { clientId, removeFromAll } = {}) => {
    try {
      const params = new URLSearchParams();
      if (clientId) params.append("clientId", clientId);
      if (removeFromAll) params.append("removeFromAll", "true");
      const qs = params.toString();
      const url = `/contacts/${id}${qs ? `?${qs}` : ""}`;

      const res = await apiRequest(url, {
        method: "DELETE",
        version: "V1",
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        return {
          success: false,
          message: errData.message || "Failed to delete contact.",
        };
      }

      const data = await res.json().catch(() => ({}));
      await fetchContacts();
      return { success: true, action: data.action, message: data.message };
    } catch (err) {
      console.error("Error deleting contact:", err);
      return { success: false, message: "An unexpected error occurred." };
    }
  };

  const resendInvite = async (email, firstName = null, lastName = null) => {
    try {
      const body = { email };
      if (firstName) {
        body.first_name = firstName;
      }
      if (lastName) {
        body.last_name = lastName;
      }

      const res = await apiRequest(`/users/resendInvite`, {
        method: "POST",
        version: "V1",
        body,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to resend invitation");
      }

      const data = await res.json();
      return { success: true, message: data.message };
    } catch (err) {
      console.error("Resend invite error:", err);
      return { success: false, message: err.message };
    }
  };

  const reorderContacts = async (clientId, contactOrder) => {
    try {
      const res = await apiRequest(`/contacts/reorder`, {
        method: "PUT",
        version: "V1",
        body: { clientId, contactOrder },
      });

      if (!res.ok) {
        const data = await res.json();
        console.error("Reorder failed:", data.message);
        return;
      }

      await fetchContacts();
    } catch (err) {
      console.error("Error reordering contacts:", err);
    }
  };

  const updateSelectedContact = (id) => {
    if (id === null) {
      setSelectedContact(null);
    } else {
      const contact = contacts.find((contact) => contact.id === id);
      setSelectedContact(contact);
    }
  };

  return (
    <ContactContext.Provider
      value={{
        regContacts,
        emergencyContacts,
        loading,
        updateContact,
        addContact,
        deleteContact,
        resendInvite,
        reorderContacts,
        updateSelectedContact,
        selectedContact,
        addExistingContact,
      }}
    >
      {children}
    </ContactContext.Provider>
  );
};

export const useContacts = () => useContext(ContactContext);
